import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns-tz';
import { motion } from 'framer-motion';
import HomeLayout from '../asset/layout/HomeLayout';
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
  alpha,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import SearchBar from '../components/mui/SearchBar';
import IOSSwitch from '../style/globalStyles';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ConfirmationModal from '../components/ConfirmationModal';
import { getAllUsersClient, updateUserStatusById } from '../actions/userAuth';
import { axiosInstance } from '../utils/api';
import CustomButton from '../components/mui/CustomButton';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import ResponsiveDatePickers from '../components/mui/PichDate';
import dayjs from 'dayjs';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import GppBadIcon from '@mui/icons-material/GppBad';
import SecurityIcon from '@mui/icons-material/Security';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import Tooltip from '@mui/material/Tooltip';
import UsersOrdersTab from './usersOrders/UsersOrdersTab';
import FraudUsersTab from './usersOrders/FraudUsersTab';
import FraudModal from '../components/FraudModal';

const columns = [
  'User',
  'Email',
  'Phone',
  'Role',
  'Points',
  'Last Login',
  'Status',
];

const formatPoints = (walletBalance, walletCurrency = 'INR') => {
  const value = Number(walletBalance);
  const formatted = Number.isFinite(value) ? value.toFixed(2) : '0.00';
  return `${formatted}`;
};

const extractUsersList = (response) => {
  const users =
    response?.data?.data?.users ||
    response?.data?.users ||
    response?.data?.content ||
    (Array.isArray(response?.data?.data) ? response.data.data : null) ||
    (Array.isArray(response?.data) ? response.data : null) ||
    [];

  return Array.isArray(users) ? users : [];
};

const getProfileImageUrl = (profile) => {
  if (!profile) return undefined;
  if (profile.startsWith('http')) return profile;
  const base = (axiosInstance.defaults.baseURL || '').replace(/\/$/, '');
  const path = profile.startsWith('/') ? profile : `/${profile}`;
  return `${base}${path}`;
};

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: '24px',
      border: '1px solid',
      borderColor: alpha('#11409B', 0.08),
      bgcolor: '#fff',
      boxShadow: '0 4px 32px rgba(15, 23, 42, 0.06)',
      overflow: 'hidden',
      ...sx,
    }}
  >
    {children}
  </Card>
);

const StatCard = ({ icon: Icon, label, value, accent, gradient }) => (
  <PanelCard sx={{ height: '100%' }}>
    <Box sx={{ p: 2.5, position: 'relative', overflow: 'hidden' }}>
      <Box
        sx={{
          position: 'absolute',
          top: -30,
          right: -30,
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(accent, 0.2)} 0%, transparent 70%)`,
        }}
      />
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '14px',
            background: gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 8px 24px ${alpha(accent, 0.35)}`,
          }}
        >
          <Icon sx={{ color: '#fff', fontSize: 24 }} />
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: '#64748b',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontSize: '0.7rem',
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.75rem',
              color: '#0f172a',
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
            }}
          >
            {value}
          </Typography>
        </Box>
      </Stack>
    </Box>
  </PanelCard>
);

const UserProfile = ({ defaultTab }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUserData, setFilteredUserData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(10);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [userToUpdate, setUserToUpdate] = useState(null);
  const [newStatus, setNewStatus] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');

  const isBlacklistPath =
    location.pathname === '/blacklist' ||
    location.pathname === '/user-blacklist' ||
    location.pathname === '/fraud-users';

  const isOrdersPath =
    location.pathname === '/user-orders' ||
    location.pathname === '/users-orders';

  const computeInitialTab = () => {
    if (defaultTab !== undefined) return defaultTab;
    if (isBlacklistPath) return 2;
    if (isOrdersPath) return 1;
    if (
      tabParam === 'blacklist' ||
      tabParam === 'blacklisted' ||
      tabParam === 'blocklist' ||
      tabParam === 'fraud' ||
      tabParam === 'fraud-users'
    ) {
      return 2;
    }
    if (tabParam === 'orders' || tabParam === 'user-orders') {
      return 1;
    }
    return 0;
  };

  const [tabValue, setTabValue] = useState(computeInitialTab);

  const [fraudModalOpen, setFraudModalOpen] = useState(false);
  const [fraudUser, setFraudUser] = useState(null);

  const handleOpenFraudModal = (user) => {
    setFraudUser(user);
    setFraudModalOpen(true);
  };

  const [selectedDateFrom, setSelectedDateFrom] = useState(null);
  const [selectedDateTo, setSelectedDateTo] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (
      isBlacklistPath ||
      tabParam === 'blacklist' ||
      tabParam === 'blacklisted' ||
      tabParam === 'blocklist' ||
      tabParam === 'fraud' ||
      tabParam === 'fraud-users'
    ) {
      setTabValue(2);
    } else if (isOrdersPath || tabParam === 'orders' || tabParam === 'user-orders') {
      setTabValue(1);
    } else if (tabParam === 'users' || (!tabParam && !isBlacklistPath && !isOrdersPath && defaultTab === undefined)) {
      setTabValue(0);
    }
  }, [tabParam, isBlacklistPath, isOrdersPath, defaultTab]);

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
    if (newValue === 2) {
      setSearchParams({ tab: 'blacklist' });
    } else if (newValue === 1) {
      setSearchParams({ tab: 'orders' });
    } else {
      setSearchParams({});
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    if (tabValue === 0) {
      getData();
    }
  }, [searchTerm, countPerPage, currentPage, selectedDateFrom, selectedDateTo, tabValue]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const getData = () => {
    setLoading(true);
    dispatch(
      getAllUsersClient(
        { page: 1, perPage: 100000 },
        (data) => {
          const filteredData = extractUsersList(data).filter((item) => {
            const matchQuery =
              item?.name
                ?.toLowerCase()
                ?.includes(searchTerm?.toLowerCase()) ||
              item?.username
                ?.toLowerCase()
                ?.includes(searchTerm?.toLowerCase()) ||
              item?.phone?.includes(searchTerm) ||
              item?.role
                ?.toLowerCase()
                ?.includes(searchTerm?.toLowerCase()) ||
              item?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
              String(item?.walletBalance ?? '').includes(searchTerm);

            const matchDateRange =
              (!selectedDateFrom ||
                new Date(item.updatedAt) >= new Date(selectedDateFrom)) &&
              (!selectedDateTo ||
                new Date(item.updatedAt) <= new Date(selectedDateTo));
            const role = String(item?.role || '').toLowerCase();
            const isEndUser = role !== 'admin' && role !== 'superadmin';
            return matchQuery && matchDateRange && isEndUser;
          });

          filteredData.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );

          setFilteredUserData(filteredData);
          setLoading(false);
        },
        () => setLoading(false)
      )
    );
  };

  const stats = useMemo(() => {
    const total = filteredUserData.length;
    const active = filteredUserData.filter((u) => u.active).length;
    return { total, active, inactive: total - active };
  }, [filteredUserData]);

  const handleConfirmationOpen = (user, status) => {
    setUserToUpdate(user);
    setNewStatus(status);
    setConfirmationOpen(true);
  };

  const handleConfirmationClose = () => setConfirmationOpen(false);

  const updateUserStatus = (id, status) => {
    dispatch(
      updateUserStatusById(
        id,
        { active: status },
        () => {
          enqueueSnackbar('Status Updated!', { variant: 'success' });
          getData();
        },
        (error) => {
          enqueueSnackbar(error?.response?.data?.message, { variant: 'error' });
        }
      )
    );
  };

  const handleConfirmUpdate = () => {
    if (userToUpdate) {
      updateUserStatus(userToUpdate.id, newStatus);
    }
    setConfirmationOpen(false);
  };

  const formatDateIndian = (dateString) => {
    const date = new Date(dateString);
    return format(date, 'dd/MM/yyyy', { timeZone: 'Asia/Kolkata' });
  };

  const exportToExcel = () => {
    const header = [
      'User Name',
      'Email',
      'Phone Number',
      'Role',
      'Points',
      'Wallet Currency',
      'Last Login',
      'Active',
    ];

    const dataToExport = filteredUserData.map((user) => [
      user?.username || user?.name,
      user?.email,
      user?.phone,
      user?.role || '—',
      Number.isFinite(Number(user?.walletBalance))
        ? Number(user.walletBalance).toFixed(2)
        : '0.00',
      user?.walletCurrency || 'INR',
      user?.updatedAt ? formatDateIndian(user?.updatedAt) : '--',
      user?.active ? 'Yes' : 'No',
    ]);

    dataToExport.unshift(header);

    const worksheet = XLSX.utils.aoa_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users Data');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/octet-stream',
    });
    saveAs(blob, 'Users_data.xlsx');
  };

  const totalPages = Math.ceil(filteredUserData.length / countPerPage) || 1;

  const paginatedData = filteredUserData.slice(
    (currentPage - 1) * countPerPage,
    currentPage * countPerPage
  );

  const handleSearchClear = () => {
    setSearchTerm('');
    setSelectedDateFrom(null);
    setSelectedDateTo(null);
    setCurrentPage(1);
  };

  const premiumButtonSx = {
    height: '44px',
    borderRadius: '12px',
    fontWeight: 600,
    textTransform: 'none',
    background: 'linear-gradient(135deg, #11409B 0%, #2563eb 100%)',
    boxShadow: '0 6px 24px rgba(37, 99, 235, 0.35)',
    '&:hover': {
      background: 'linear-gradient(135deg, #0f3580 0%, #1d4ed8 100%)',
    },
  };

  const getInitials = (name, email) => {
    const src = name || email || '?';
    return src.charAt(0).toUpperCase();
  };

  return (
    <HomeLayout>
      <Box
        sx={{
          minHeight: '100%',
          background:
            'linear-gradient(180deg, #f0f4fa 0%, #f5f7fa 35%, #eef2f7 100%)',
          mx: -3,
          mt: -3,
          px: 3,
          pt: 3,
          pb: 4,
        }}
      >
        {/* Hero */}
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3,
              borderRadius: '24px',
              background:
                'linear-gradient(135deg, #0c3483 0%, #11409B 40%, #1e40af 70%, #2563eb 100%)',
              color: '#fff',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(17, 64, 155, 0.35)',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -50,
                right: -30,
                width: 200,
                height: 200,
                borderRadius: '50%',
                bgcolor: alpha('#fff', 0.06),
              }}
            />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={2}
              sx={{ position: 'relative', zIndex: 1 }}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  {tabValue === 0 ? (
                    <GroupOutlinedIcon />
                  ) : tabValue === 1 ? (
                    <ShoppingBagOutlinedIcon />
                  ) : (
                    <GppBadIcon />
                  )}
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, letterSpacing: '0.1em', opacity: 0.85 }}
                  >
                    {tabValue === 0
                      ? 'USER MANAGEMENT'
                      : tabValue === 1
                      ? "USER'S ORDERS"
                      : 'USER BLACKLIST & SECURITY'}
                  </Typography>
                </Stack>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}
                >
                  {tabValue === 0
                    ? 'Users dashboard'
                    : tabValue === 1
                    ? "User's orders & transactions"
                    : 'Blacklisted Users & Fraud Lockouts'}
                </Typography>
                <Typography sx={{ mt: 1, opacity: 0.82, maxWidth: 540 }}>
                  {tabValue === 0
                    ? 'Search, filter, and manage end-user accounts. Toggle active status or export your filtered list.'
                    : tabValue === 1
                    ? "View paginated users with order metrics, real-time status breakdowns (Success, Pending, Failed), and detailed orders."
                    : 'Monitor system auto-flagged accounts (≥3 daily failures) and admin blacklists, inspect lockout reasons, and restore user accounts.'}
                </Typography>
              </Box>
              {tabValue === 0 && (
                <CustomButton
                  variant="contained"
                  onClick={exportToExcel}
                  startIcon={<FileDownloadOutlinedIcon />}
                  sx={{
                    ...premiumButtonSx,
                    bgcolor: alpha('#fff', 0.15),
                    backdropFilter: 'blur(8px)',
                    border: `1px solid ${alpha('#fff', 0.25)}`,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: alpha('#fff', 0.22) },
                  }}
                >
                  Export users
                </CustomButton>
              )}
            </Stack>
          </Box>
        </motion.div>

        {/* Navigation Tabs */}
        <Box
          sx={{
            mb: 3,
            bgcolor: '#fff',
            borderRadius: '16px',
            border: `1px solid ${alpha('#11409B', 0.1)}`,
            boxShadow: '0 4px 24px rgba(15, 23, 42, 0.05)',
            px: 1,
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="user management tabs"
            sx={{
              minHeight: 48,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                color: '#64748b',
                minHeight: 48,
              },
              '& .Mui-selected': { color: '#11409B' },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                bgcolor: '#11409B',
              },
            }}
          >
            <Tab label="All Users" />
            <Tab label="User's Orders" />
            <Tab
              label="Blacklist"
              icon={<GppBadIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {tabValue === 0 ? (
          <>
            {/* Stats */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <motion.div custom={0} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={GroupOutlinedIcon}
                label="Total users"
                value={stats.total}
                accent="#4f46e5"
                gradient="linear-gradient(135deg, #818cf8 0%, #4f46e5 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={4}>
            <motion.div custom={1} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={PersonOutlineOutlinedIcon}
                label="Active"
                value={stats.active}
                accent="#059669"
                gradient="linear-gradient(135deg, #34d399 0%, #059669 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={4}>
            <motion.div custom={2} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={PersonOffOutlinedIcon}
                label="Inactive"
                value={stats.inactive}
                accent="#64748b"
                gradient="linear-gradient(135deg, #94a3b8 0%, #64748b 100%)"
              />
            </motion.div>
          </Grid>
        </Grid>

        {/* Filters */}
        <motion.div custom={3} initial="hidden" animate="visible" variants={fadeUp}>
          <PanelCard sx={{ mb: 3, p: { xs: 2, md: 2.5 } }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <FilterListOutlinedIcon sx={{ color: '#11409B', fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                Filters
              </Typography>
            </Stack>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <SearchBar onSearch={handleSearch} searchTerm={searchTerm} height="2.75rem" />
              </Grid>
              <Grid item xs={12} md={3}>
                <ResponsiveDatePickers
                  selectedDate={selectedDateFrom}
                  setSelectedDate={setSelectedDateFrom}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <ResponsiveDatePickers
                  selectedDate={selectedDateTo}
                  setSelectedDate={setSelectedDateTo}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomButton
                  variant="outlined"
                  onClick={handleSearchClear}
                  fullWidth
                  sx={{
                    height: '2.75rem',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: alpha('#11409B', 0.25),
                    color: '#11409B',
                    '&:hover': {
                      borderColor: '#11409B',
                      bgcolor: alpha('#11409B', 0.04),
                    },
                  }}
                >
                  Clear
                </CustomButton>
              </Grid>
            </Grid>
          </PanelCard>
        </motion.div>

        {/* Table */}
        <motion.div custom={4} initial="hidden" animate="visible" variants={fadeUp}>
          {loading ? (
            <PanelCard>
              <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
                <CircularProgress sx={{ color: '#11409B' }} />
                <Typography sx={{ mt: 2, color: '#64748b' }}>
                  Loading users…
                </Typography>
              </Stack>
            </PanelCard>
          ) : paginatedData?.length > 0 ? (
            <TableContainer component={PanelCard}>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha('#11409B', 0.04),
                      '& th': {
                        borderBottom: `1px solid ${alpha('#11409B', 0.08)}`,
                      },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column}
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          color: '#11409B',
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          py: 2,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {column}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.filter((row) => row?.role !== 'SUPERADMIN')?.map((row) => (
                    <TableRow
                      key={row.id || row._id || row.email}
                      sx={{
                        transition: 'background 0.2s',
                        '&:hover': { bgcolor: alpha('#11409B', 0.03) },
                        '& td': {
                          borderBottom: `1px solid ${alpha('#11409B', 0.06)}`,
                          py: 1.75,
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar
                            src={
                              getProfileImageUrl(row?.profile) || row?.pictureUrl
                            }
                            alt={row?.username || row?.name}
                            sx={{
                              width: 44,
                              height: 44,
                              bgcolor: 'linear-gradient(135deg, #11409B, #2563eb)',
                              fontWeight: 700,
                              fontSize: '1rem',
                              border: '2px solid #fff',
                              boxShadow: '0 4px 12px rgba(17,64,155,0.2)',
                            }}
                          >
                            {getInitials(row?.name || row?.username, row?.email)}
                          </Avatar>
                          <Typography sx={{ fontWeight: 600, color: '#0f172a' }}>
                            { row?.name || '—'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#475569' }}>
                          {row?.email || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#475569' }}>
                          {row?.phone ? `+91 ${row.phone}` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row?.role || '—'}
                          size="small"
                          sx={{
                            bgcolor: alpha('#7c3aed', 0.1),
                            color: '#6d28d9',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                          }}
                        />
                      </TableCell>
                      
                      
                      <TableCell>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: '#11409B',
                          }}
                        >
                          {formatPoints(row?.walletBalance, row?.walletCurrency)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          {row.updatedAt
                            ? dayjs(row.updatedAt).format('DD MMM YYYY')
                            : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={1}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Chip
                              label={row.active ? 'Active' : 'Inactive'}
                              size="small"
                              sx={{
                                bgcolor: row.active
                                  ? alpha('#16a34a', 0.12)
                                  : alpha('#64748b', 0.12),
                                color: row.active ? '#16a34a' : '#64748b',
                                fontWeight: 700,
                                fontSize: '0.7rem',
                              }}
                            />
                            <FormControlLabel
                              control={
                                <IOSSwitch
                                  checked={row.active}
                                  onChange={(e) =>
                                    handleConfirmationOpen(row, e.target.checked)
                                  }
                                />
                              }
                              label=""
                              sx={{ m: 0 }}
                            />
                          </Stack>

                          {/* Blacklist Badge & Action */}
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            {row.isFraud ? (
                              <Tooltip
                                title={
                                  <Box sx={{ p: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: '#fca5a5' }}>
                                      BLACKLISTED & LOCKED
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                      Reason: {row.fraudReason || 'Suspicious activity'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', color: '#cbd5e1' }}>
                                      By: {row.fraudFlaggedBy || 'SYSTEM'} •{' '}
                                      {row.fraudFlaggedAt ? dayjs(row.fraudFlaggedAt).format('DD MMM YYYY, HH:mm') : ''}
                                    </Typography>
                                  </Box>
                                }
                              >
                                <Chip
                                  icon={<GppBadIcon style={{ fontSize: 13, color: '#dc2626' }} />}
                                  label="BLACKLISTED"
                                  size="small"
                                  onClick={() => handleOpenFraudModal(row)}
                                  sx={{
                                    bgcolor: alpha('#dc2626', 0.12),
                                    color: '#dc2626',
                                    fontWeight: 800,
                                    fontSize: '0.68rem',
                                    border: '1px solid rgba(220, 38, 38, 0.25)',
                                    cursor: 'pointer',
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Blacklist user & lock account">
                                <CustomButton
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleOpenFraudModal(row)}
                                  startIcon={<SecurityIcon style={{ fontSize: 13 }} />}
                                  sx={{
                                    fontSize: '0.68rem',
                                    py: 0.2,
                                    px: 1,
                                    minHeight: 22,
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderColor: alpha('#dc2626', 0.25),
                                    color: '#dc2626',
                                    '&:hover': {
                                      bgcolor: alpha('#dc2626', 0.06),
                                      borderColor: '#dc2626',
                                    },
                                  }}
                                >
                                  Blacklist
                                </CustomButton>
                              </Tooltip>
                            )}

                            {row.isFraud && (
                              <Tooltip title="Remove from blacklist & restore user account">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenFraudModal(row)}
                                  sx={{
                                    p: 0.5,
                                    color: '#16a34a',
                                    bgcolor: alpha('#16a34a', 0.08),
                                    '&:hover': { bgcolor: alpha('#16a34a', 0.16) },
                                  }}
                                >
                                  <LockOpenIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <PanelCard>
              <Stack alignItems="center" sx={{ py: 8, px: 3 }}>
                <GroupOutlinedIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  No users found
                </Typography>
                <Typography sx={{ color: '#64748b', mt: 1, textAlign: 'center' }}>
                  Try adjusting your search or date filters.
                </Typography>
              </Stack>
            </PanelCard>
          )}
        </motion.div>

        {/* Pagination */}
        {!loading && filteredUserData.length > 0 && (
          <motion.div custom={5} initial="hidden" animate="visible" variants={fadeUp}>
            <PanelCard sx={{ mt: 3, p: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Typography sx={{ color: '#64748b', fontWeight: 500 }}>
                  Showing {(currentPage - 1) * countPerPage + 1}–
                  {Math.min(currentPage * countPerPage, filteredUserData.length)} of{' '}
                  {filteredUserData.length}
                </Typography>

                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Rows per page
                  </Typography>
                  <Box
                    component="select"
                    value={countPerPage}
                    onChange={(e) => {
                      setCountPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    sx={{
                      border: `1px solid ${alpha('#11409B', 0.2)}`,
                      borderRadius: '10px',
                      px: 1.5,
                      py: 0.75,
                      fontSize: '0.875rem',
                      color: '#0f172a',
                      bgcolor: '#fff',
                      outline: 'none',
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={30}>30</option>
                  </Box>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      sx={{
                        border: '1px solid',
                        borderColor: alpha('#11409B', 0.15),
                        borderRadius: '12px',
                        bgcolor: '#fff',
                        '&:hover': { bgcolor: alpha('#11409B', 0.06) },
                      }}
                    >
                      <KeyboardArrowLeftIcon />
                    </IconButton>
                    <Typography sx={{ fontWeight: 600, minWidth: 48, textAlign: 'center' }}>
                      {currentPage} / {totalPages}
                    </Typography>
                    <IconButton
                      onClick={() =>
                        setCurrentPage((p) => Math.min(p + 1, totalPages))
                      }
                      disabled={currentPage >= totalPages}
                      sx={{
                        border: '1px solid',
                        borderColor: alpha('#11409B', 0.15),
                        borderRadius: '12px',
                        bgcolor: '#fff',
                        '&:hover': { bgcolor: alpha('#11409B', 0.06) },
                      }}
                    >
                      <KeyboardArrowRightIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Stack>
            </PanelCard>
          </motion.div>
        )}

        <ConfirmationModal
          open={confirmationOpen}
          handleClose={handleConfirmationClose}
          handleConfirm={handleConfirmUpdate}
        />
          </>
        ) : tabValue === 1 ? (
          <UsersOrdersTab />
        ) : (
          <FraudUsersTab />
        )}

        <FraudModal
          open={fraudModalOpen}
          onClose={() => {
            setFraudModalOpen(false);
            setFraudUser(null);
          }}
          user={fraudUser}
          onSuccess={() => {
            if (tabValue === 0) getData();
          }}
        />
      </Box>
    </HomeLayout>
  );
};

export default UserProfile;
